import React from 'react';
import { Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { ChangeEvent, useContext, useState } from 'react';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import * as yup from 'yup';
import { useFormik } from 'formik';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import PasswordTextField from 'shared/lib/components/controls/textFields/passwordTextField';
import { useTranslation } from 'react-i18next';
import { regulars } from 'shared/lib/utils/globalUtils';
import EmailTextField from 'shared/lib/components/controls/textFields/emailTextField';

const NAME_MAX_LENGTH = 100;
const SURNAME_MAX_LENGTH = 100;

export type CreateUserData = {
  name: string;
  surname: string;
  email: string;
  password: string;
};

export default observer(function CreateUserForm({
  onSubmit,
}: {
  onSubmit: (data: CreateUserData) => Promise<void>;
}) {
  // @ts-ignore
  const { t } = useTranslation();

  const {
    mobxStore: { authStore },
  } = useContext(MobxContext);
  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);

  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = yup.object({
    name: yup
      .string()
      // @ts-ignore
      .required(t('errors.validationSchema.required'))
      .matches(
        regulars.onlyKirilic,
        t('errors.validationSchema.invalidSymbols')
      )
      .max(
        NAME_MAX_LENGTH,
        t('errors.validationSchema.nameTooLong', { maxLength: NAME_MAX_LENGTH })
      ),
    surname: yup
      .string()
      // @ts-ignore
      .required(t('errors.validationSchema.required'))
      .matches(
        regulars.onlyKirilic,
        t('errors.validationSchema.invalidSymbols')
      )
      .max(
        SURNAME_MAX_LENGTH,
        t('errors.validationSchema.surnameTooLong', {
          maxLength: SURNAME_MAX_LENGTH,
        })
      ),
    email: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .email(t('errors.validationSchema.email')),
    password: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .min(8, t('errors.validationSchema.passwordTooShort')),
  });

  const [formikSubmited, setFormikSubmited] = useState(false);
  const formik = useFormik({
    initialValues: {
      name: '',
      surname: '',
      email: '',
      password: '',
      passwordConfirm: '',
    },
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async (signupData) => {
      try {
        setIsLoading(true);
        await onSubmit({
          ...signupData,
        });
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        console.error(e);
      }
    },
  });

  const isFormikFieldError = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && !!formik.errors[field];

  const getFormikFieldErrorText = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && formik.errors[field];

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    authStore.clearError();
    formik.handleChange(e);
  };

  return (
    <>
      <form
        id="signupForm"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: theme.spacing(sp.s60),
        }}
        onSubmit={formik.handleSubmit}
      >
        <CustomTextField
          label={t('login.name')}
          id="name"
          name="name"
          typographyVariantSize="f16"
          labelTypographyVariantSize="f12"
          placeholder="Укажите имя"
          wrapperSxProps={{
            width: 1,
          }}
          value={formik.values.name}
          onChange={handleOnChange}
          onBlur={(e) => {
            formik.handleBlur(e);
          }}
          error={isFormikFieldError('name')}
          helperText={getFormikFieldErrorText('name')}
          autoFocus={true}
        ></CustomTextField>

        <CustomTextField
          label={t('login.surname')}
          id="surname"
          name="surname"
          typographyVariantSize="f16"
          labelTypographyVariantSize="f12"
          placeholder="Укажите фамилию"
          wrapperSxProps={{
            width: 1,
          }}
          value={formik.values.surname}
          onChange={handleOnChange}
          onBlur={(e) => {
            formik.handleBlur(e);
          }}
          error={isFormikFieldError('surname')}
          helperText={getFormikFieldErrorText('surname')}
        ></CustomTextField>

        <EmailTextField
          label={t('login.email')}
          id="email"
          name="email"
          placeholder={t('login.emailPlaceholder')}
          value={formik.values.email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleOnChange(e);
          }}
          onBlur={(e) => {
            formik.handleBlur(e);
          }}
          error={isFormikFieldError('email')}
          helperText={getFormikFieldErrorText('email')}
        ></EmailTextField>

        <PasswordTextField
          label={t('login.password')}
          id="password"
          name="password"
          placeholder={t('login.passwordPlaceholder')}
          value={formik.values.password}
          onChange={handleOnChange}
          onBlur={(e) => {
            formik.handleBlur(e);
          }}
          error={isFormikFieldError('password')}
          helperText={getFormikFieldErrorText('password')}
        ></PasswordTextField>
      </form>

      <PrimaryButton
        sx={{
          width: 1,
        }}
        form="signupForm"
        variant="contained"
        type="submit"
        loading={isLoading}
        onClick={() => {
          setFormikSubmited(true);
        }}
      >
        <Typography variant="f20">Создать пользователя</Typography>
      </PrimaryButton>
    </>
  );
});
